import type { PmiElementComparisonItem, PmiElementState } from '../components/PmiElementComparison';

interface PmiElementRange {
  code?: string;
  max?: number;
  min?: number;
}

function toRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function asText(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function asOptionalNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value.trim().replace('%', ''));
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  return undefined;
}

function parseJsonValue(value: unknown) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function readText(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = asText(record[key]).trim();
    if (value) {
      return value;
    }
  }
  return '';
}

function readOptionalNumber(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = asOptionalNumber(record[key]);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function readPmiRequired(record: Record<string, unknown>) {
  const raw = record.pmi_required ?? record.pmiRequired ?? record.is_pmi_required ?? record.isPmiRequired;
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'number') {
    return raw !== 0;
  }
  const text = asText(raw).trim().toLowerCase();
  if (['1', 'true', 'yes', 'required', 'enabled', '是', '启用'].includes(text)) {
    return true;
  }
  if (['0', 'false', 'no', 'disabled', '否', '停用'].includes(text)) {
    return false;
  }
  return undefined;
}

function formatPmiNumber(value: number) {
  return value.toFixed(3);
}

function formatPmiValue(value: unknown) {
  const numeric = asOptionalNumber(value);
  return numeric !== undefined ? `${formatPmiNumber(numeric)}%` : asText(value) || '--';
}

function formatPmiRange(min?: number, max?: number) {
  if (min !== undefined && max !== undefined) {
    return `要求 ${formatPmiNumber(min)} - ${formatPmiNumber(max)}%`;
  }
  if (min !== undefined) {
    return `要求 ≥ ${formatPmiNumber(min)}%`;
  }
  if (max !== undefined) {
    return `要求 ≤ ${formatPmiNumber(max)}%`;
  }
  return '未取得 PMI 标准';
}

function normalizeElementKey(value: unknown) {
  return asText(value).trim().toUpperCase();
}

function parseRangeText(value: unknown): PmiElementRange | null {
  const text = asText(value).trim();
  if (!text) {
    return null;
  }

  const rangeMatch = text.match(/(-?\d+(?:\.\d+)?)\s*(?:-|~|–|—|至|到)\s*(-?\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      max: asOptionalNumber(rangeMatch[2]),
      min: asOptionalNumber(rangeMatch[1]),
    };
  }

  const minMatch = text.match(/(?:min|下限|≥|>=)\s*[:：]?\s*(-?\d+(?:\.\d+)?)/i);
  const maxMatch = text.match(/(?:max|上限|≤|<=)\s*[:：]?\s*(-?\d+(?:\.\d+)?)/i);
  const min = minMatch ? asOptionalNumber(minMatch[1]) : undefined;
  const max = maxMatch ? asOptionalNumber(maxMatch[1]) : undefined;
  return min !== undefined || max !== undefined ? { max, min } : null;
}

export function readPmiRangeFromRecord(record: Record<string, unknown>): PmiElementRange | null {
  const min = readOptionalNumber(record, ['pmi_min', 'pmiMin', 'min', 'min_value', 'lower_limit', 'minimum']);
  const max = readOptionalNumber(record, ['pmi_max', 'pmiMax', 'max', 'max_value', 'upper_limit', 'maximum']);
  return min !== undefined || max !== undefined
    ? { max, min }
    : parseRangeText(record.range || record.pmi_range || record.requirement || record.value);
}

function putPmiRange(rangeMap: Map<string, PmiElementRange>, code: unknown, range: PmiElementRange | null) {
  const key = normalizeElementKey(code);
  if (!key || !range || (range.min === undefined && range.max === undefined)) {
    return;
  }
  rangeMap.set(key, {
    ...range,
    code: asText(code).trim() || range.code || key,
  });
}

function addPmiRangeSource(rangeMap: Map<string, PmiElementRange>, source: unknown) {
  const parsed = parseJsonValue(source);
  if (Array.isArray(parsed)) {
    const records = parsed.map(toRecord).filter((record): record is Record<string, unknown> => Boolean(record));
    const hasRequiredFlags = records.some((record) => readPmiRequired(record) !== undefined);
    records.forEach((record) => {
      if (!hasRequiredFlags || readPmiRequired(record) === true) {
        putPmiRange(rangeMap, readText(record, ['symbol', 'element', 'element_code', 'code', 'name']), readPmiRangeFromRecord(record));
      }
    });
    return;
  }

  const record = toRecord(parsed);
  if (!record) {
    const rangeRegex = /([A-Za-z][A-Za-z0-9]*)\s*[:：]?\s*(-?\d+(?:\.\d+)?)\s*(?:-|~|–|—|至|到)\s*(-?\d+(?:\.\d+)?)/g;
    let match = rangeRegex.exec(asText(parsed));
    while (match) {
      putPmiRange(rangeMap, match[1], {
        max: asOptionalNumber(match[3]),
        min: asOptionalNumber(match[2]),
      });
      match = rangeRegex.exec(asText(parsed));
    }
    return;
  }

  const nested = record.elements || record.items || record.list || record.data;
  if (Array.isArray(nested)) {
    addPmiRangeSource(rangeMap, nested);
  }

  const entries = Object.entries(record);
  const hasRequiredFlags = entries.some(([, value]) => {
    const valueRecord = toRecord(value);
    return valueRecord ? readPmiRequired(valueRecord) !== undefined : false;
  });
  entries.forEach(([key, value]) => {
    const valueRecord = toRecord(value);
    if (valueRecord) {
      if (hasRequiredFlags && readPmiRequired(valueRecord) !== true) {
        return;
      }
      putPmiRange(
        rangeMap,
        readText(valueRecord, ['symbol', 'element', 'element_code', 'code', 'name']) || key,
        readPmiRangeFromRecord(valueRecord),
      );
    } else if (Array.isArray(value) && value.length >= 2) {
      putPmiRange(rangeMap, key, {
        max: asOptionalNumber(value[1]),
        min: asOptionalNumber(value[0]),
      });
    } else {
      putPmiRange(rangeMap, key, parseRangeText(value));
    }
  });
}

function buildPmiRangeMap(...sources: unknown[]) {
  const rangeMap = new Map<string, PmiElementRange>();
  sources.forEach((source) => addPmiRangeSource(rangeMap, source));
  return rangeMap;
}

export function hasPmiRangeSource(source: unknown) {
  return buildPmiRangeMap(source).size > 0;
}

function getElementState(record: Record<string, unknown>, value?: number, min?: number, max?: number): PmiElementState {
  const explicit = record.passed ?? record.pass ?? record.in_range ?? record.qualified;
  if (typeof explicit === 'boolean') {
    return explicit ? 'pass' : 'fail';
  }
  if (typeof explicit === 'number') {
    return explicit ? 'pass' : 'fail';
  }

  const explicitText = asText(explicit).trim().toLowerCase();
  if (['true', '1', 'pass', 'passed', 'ok', 'qualified', '合格'].includes(explicitText)) {
    return 'pass';
  }
  if (['false', '0', 'fail', 'failed', 'ng', 'unqualified', '不合格'].includes(explicitText)) {
    return 'fail';
  }

  const resultText = readText(record, ['result', 'state', 'status', 'final_result']).toLowerCase();
  if (['pass', 'passed', 'ok', 'qualified', '合格'].includes(resultText)) {
    return 'pass';
  }
  if (['fail', 'failed', 'ng', 'unqualified', '不合格'].includes(resultText)) {
    return 'fail';
  }

  if (value !== undefined && (min !== undefined || max !== undefined)) {
    return (min !== undefined && value < min) || (max !== undefined && value > max) ? 'fail' : 'pass';
  }
  return 'pending';
}

function normalizePmiElementRecord(item: unknown, rangeMap: Map<string, PmiElementRange>) {
  const record = toRecord(item);
  if (!record) {
    return null;
  }

  const code = readText(record, ['symbol', 'element', 'element_code', 'code', 'name']);
  if (!code) {
    return null;
  }

  const rawValue = record.value
    ?? record.actual
    ?? record.actual_value
    ?? record.measured
    ?? record.measured_value
    ?? record.concentration
    ?? record.content
    ?? record.result_value
    ?? record.percent;
  const value = asOptionalNumber(rawValue);
  const fallbackRange = rangeMap.get(normalizeElementKey(code));
  const min = readOptionalNumber(record, ['pmi_min', 'min', 'min_value', 'lower', 'lower_limit', 'minimum']) ?? fallbackRange?.min;
  const max = readOptionalNumber(record, ['pmi_max', 'max', 'max_value', 'upper', 'upper_limit', 'maximum']) ?? fallbackRange?.max;
  const state = getElementState(record, value, min, max);

  return {
    code,
    rangeText: formatPmiRange(min, max),
    state,
    stateText: state === 'pass' ? '合格' : state === 'fail' ? '不合格' : '未判定',
    valueText: formatPmiValue(rawValue),
  } as PmiElementComparisonItem;
}

function normalizePmiElements(source: unknown, rangeMap: Map<string, PmiElementRange>) {
  const parsed = parseJsonValue(source);
  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => normalizePmiElementRecord(item, rangeMap))
      .filter((item): item is PmiElementComparisonItem => Boolean(item));
  }

  const record = toRecord(parsed);
  return record
    ? Object.entries(record)
      .map(([code, value]) => normalizePmiElementRecord({ code, value }, rangeMap))
      .filter((item): item is PmiElementComparisonItem => Boolean(item))
    : [];
}

function collectCandidateRecords(source: unknown, depth = 0, visited = new Set<object>()): Record<string, unknown>[] {
  const parsed = parseJsonValue(source);
  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) => collectCandidateRecords(item, depth, visited));
  }

  const record = toRecord(parsed);
  if (!record || visited.has(record) || depth > 3) {
    return [];
  }
  visited.add(record);

  const records = Array.isArray(record.records) ? record.records : [];
  const failures = Array.isArray(record.failures) ? record.failures : [];
  const units = Array.isArray(record.units) ? record.units : [];
  const nested = [
    record.current_unit,
    record.currentUnit,
    record.latest_record,
    record.latestRecord,
    record.record,
    record.pmi_record,
    record.pmiRecord,
    record.spectrum_result,
    record.spectrumResult,
    record.result_detail,
    record.resultDetail,
    ...units,
    ...records,
    ...failures,
  ].flatMap((item) => collectCandidateRecords(item, depth + 1, visited));

  return [...nested, record];
}

function getRangeSources(record: Record<string, unknown>) {
  return [
    record.pmi_inspection,
    record.pmiInspection,
    record.pmi_ranges,
    record.pmiRange,
    record.pmi_range,
    record.requirement_elements,
    record.pmi_requirement_elements,
  ];
}

function getDetectedSources(record: Record<string, unknown>) {
  return [
    record.element_results,
    record.elements_result,
    record.elementResults,
    record.spectrum_elements,
    record.spectrumElements,
    record.pmi_elements,
    record.pmiElements,
    record.analysis_elements,
    record.analysisElements,
    record.inspection_elements,
    record.inspectionElements,
    record.elements,
  ];
}

function sortDetectedItems(items: PmiElementComparisonItem[]) {
  return [...items].sort((first, second) => {
    if (first.state === second.state) {
      return 0;
    }
    if (first.state === 'fail') {
      return -1;
    }
    if (second.state === 'fail') {
      return 1;
    }
    return first.state === 'pass' ? -1 : 1;
  });
}

function buildMissingItems(detected: PmiElementComparisonItem[], rangeMap: Map<string, PmiElementRange>) {
  const detectedCodes = new Set(detected.map((item) => normalizeElementKey(item.code)));
  return Array.from(rangeMap.entries()).reduce<PmiElementComparisonItem[]>((items, [key, range]) => {
    if (!detectedCodes.has(key)) {
      items.push({
        code: range.code || key,
        rangeText: formatPmiRange(range.min, range.max),
        state: 'fail',
        stateText: '未检测',
        valueText: '未检出',
      });
    }
    return items;
  }, []);
}

export function buildPmiElementComparisonItems(source: unknown) {
  const candidates = collectCandidateRecords(source);
  const rangeMap = buildPmiRangeMap(...candidates.slice().reverse().flatMap(getRangeSources));
  const detectedItems = new Map<string, PmiElementComparisonItem>();

  for (const candidate of candidates) {
    for (const detectedSource of getDetectedSources(candidate)) {
      const items = normalizePmiElements(detectedSource, rangeMap);
      items.forEach((item) => {
        const key = normalizeElementKey(item.code);
        if (key && !detectedItems.has(key)) {
          detectedItems.set(key, item);
        }
      });
    }
  }

  const detected = Array.from(detectedItems.values());
  return detected.length || rangeMap.size
    ? [...sortDetectedItems(detected), ...buildMissingItems(detected, rangeMap)]
    : [];
}
